// src/components/CardTypeModal.tsx

import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useCareerCard } from '../context/CareerCardContext';
import { CardType } from '../types/careerCard';
import { CardSuggestions } from './CardSuggestions';

interface CardTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (type: CardType, initialData?: Record<string, unknown>) => void;
}

const CardTypeModal: React.FC<CardTypeModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { schemas } = useCareerCard();
  const activeSchemas = schemas.filter(schema => schema.is_active);
  const [selectedType, setSelectedType] = useState<CardType | null>(null);
  const [title, setTitle] = useState('');
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [initialCardData, setInitialCardData] = useState<Record<string, unknown> | null>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [hasGeneratedSuggestions, setHasGeneratedSuggestions] = useState(false);

  const handleSuggestionSelect = (type: CardType, initialData: Record<string, unknown>) => {
    setSelectedType(type);
    setTitle(initialData.title as string || '');
    setInitialCardData(initialData);
    setShowTitleInput(true);
  };

  const handleTypeSelect = (type: CardType) => {
    setSelectedType(type);
    setInitialCardData(null);
    setShowTitleInput(true);
  };

  const handleTitleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedType && title.trim()) {
      const finalData = {
        ...(initialCardData || {}),
        title: title.trim()
      };
      onSubmit(selectedType, finalData);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setTitle('');
    setShowTitleInput(false);
    setInitialCardData(null);
    setSuggestions([]);
    setHasGeneratedSuggestions(false);
    onClose();
  };

  const handleBack = () => {
    setShowTitleInput(false);
    setTitle('');
  };

  const handleSuggestionsGenerated = useCallback((newSuggestions: any[]) => {
    setSuggestions(newSuggestions);
    setHasGeneratedSuggestions(true);
  }, []);

  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(false);

  useEffect(() => {
    if (isOpen && !showTitleInput) {
      setShouldRenderSuggestions(true);
    } else {
      setShouldRenderSuggestions(false);
    }
  }, [isOpen, showTitleInput]);

  // Add helper function to format field names
  const formatFieldName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Add helper function to render nested field content
  const renderNestedContent = (content: any): React.ReactNode => {
    if (!content) return null;

    // If content is a string that looks like JSON, try to parse it
    if (typeof content === 'string' && (content.startsWith('[') || content.startsWith('{'))) {
      try {
        content = JSON.parse(content);
      } catch (e) {
        return <span className="text-sm text-gray-600 dark:text-gray-400">{content}</span>;
      }
    }

    if (Array.isArray(content)) {
      return (
        <div className="space-y-2">
          {content.map((item, index) => (
            <div key={index} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              {typeof item === 'object' ? (
                Object.entries(item).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium">{formatFieldName(key)}:</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">
                      {value as string}
                    </span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof content === 'object' && content !== null) {
      return (
        <div className="space-y-1">
          {Object.entries(content).map(([key, value]) => (
            <div key={key} className="text-sm">
              <span className="font-medium">{formatFieldName(key)}:</span>{' '}
              {renderNestedContent(value)}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-sm text-gray-600 dark:text-gray-400">{String(content)}</span>;
  };

  return (
    <Transition
      show={isOpen}
      appear
      as={Fragment}
    >
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform rounded-lg bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white">
                    {showTitleInput ? 'Enter Card Title' : 'Choose Card Type'}
                  </Dialog.Title>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {showTitleInput ? (
                    <form onSubmit={handleTitleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="cardTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Title for your new {selectedType} card
                        </label>
                        <input
                          type="text"
                          id="cardTitle"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Enter a title for your card"
                          className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          autoFocus
                          required
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={handleBack}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={!title.trim()}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Create Card
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      {shouldRenderSuggestions && (
                        <CardSuggestions 
                          onSelect={handleSuggestionSelect} 
                          existingSuggestions={suggestions}
                          hasGeneratedSuggestions={hasGeneratedSuggestions}
                          onSuggestionsGenerated={handleSuggestionsGenerated}
                        />
                      )}

                      <div className="pt-2">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                          All Card Types
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {activeSchemas.map((schema) => (
                            <button
                              key={schema.card_type}
                              onClick={() => handleTypeSelect(schema.card_type as CardType)}
                              className="text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors duration-200"
                            >
                              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                                {schema.display_name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                Select to create a new {schema.display_name.toLowerCase()} card
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {!showTitleInput && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CardTypeModal;
